import { Venue, VenuePinType } from "common/types";
import { cn } from "@/lib/utils";

import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";
import GreenPin from "@/assets/pins/green_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import GreyPinSelected from "@/assets/pins/grey_pin_selected.svg";
import OrangePinSelected from "@/assets/pins/orange_pin_selected.svg";

// Pin sizes match the legacy Marker icon sizes exactly.
const SIZE_DEFAULT = 30;
const SIZE_ACTIVE = 42;
const SIZE_SELECTED = 40;

function getPinSvg(venue: Venue, isSelected: boolean, isMapperView: boolean): string {
  if (isSelected) {
    return venue.venuePinType === VenuePinType.Grey ? GreyPinSelected : OrangePinSelected;
  }

  if (!isMapperView) {
    return venue.venuePinType === VenuePinType.Grey ? GreyPin : OrangePin;
  }

  switch (venue.venuePinType) {
    case VenuePinType.Green:
      return GreenPin;
    case VenuePinType.Black:
      return BlackPin;
    case VenuePinType.Blue:
      return BluePin;
    case VenuePinType.Purple:
      return PurplePin;
    case VenuePinType.Orange:
      return OrangePin;
    case VenuePinType.Grey:
    default:
      return GreyPin;
  }
}

interface AdvancedPinContentProps {
  venue: Venue;
  isSelected: boolean;
  isHovered: boolean;
  isMapperView: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * HTML content for an Advanced Marker pin.
 *
 * The container width/height matches the SVG so the AdvancedMarkerElement's
 * default anchor (bottom-center of the element) sits at the tip of the pin.
 * The venue name label is absolutely positioned below the image so it does
 * not shift the anchor point.
 */
export function AdvancedPinContent({
  venue,
  isSelected,
  isHovered,
  isMapperView,
  onMouseEnter,
  onMouseLeave,
}: AdvancedPinContentProps) {
  const isActive = isSelected || isHovered;
  const size = isSelected ? SIZE_SELECTED : isActive ? SIZE_ACTIVE : SIZE_DEFAULT;
  const svgUrl = getPinSvg(venue, isSelected, isMapperView);

  return (
    <div
      // role="img" is non-interactive so jsx-a11y does not require tabIndex.
      // The outer <gmp-advanced-marker> element carries role="button" + tabindex="0"
      // automatically via gmpClickable=true (set by vis.gl when onClick is provided).
      role="img"
      aria-label={venue.name || "Map pin"}
      className="adv-pin-content relative flex items-end justify-center cursor-pointer"
      style={{ width: size, height: size }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <img
        src={svgUrl}
        width={size}
        height={size}
        alt={venue.name || ""}
        draggable={false}
        style={{ display: "block" }}
      />

      {/* Venue name — absolutely below the image, visible only when selected */}
      {venue.name && (
        <span
          className={cn(
            "absolute left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[10px] font-medium",
            "text-white bg-black/70 whitespace-nowrap max-w-[140px] truncate",
            "transition-opacity duration-150 pointer-events-none select-none",
            isSelected ? "opacity-100" : "opacity-0",
          )}
          style={{ top: "100%", marginTop: 2 }}
        >
          {venue.name}
        </span>
      )}
    </div>
  );
}
